/**
 * Beta Feedback Widget
 * One-of-a-kind interactive feedback experience: wizard flow, mood picker,
 * contextual copy, screenshot capture, confetti, and exit intercept.
 */

import { useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import {
  MessageSquare,
  X,
  Send,
  Star,
  Bug,
  Lightbulb,
  ThumbsUp,
  Sparkles,
  Camera,
  ChevronRight,
  Heart,
  Frown,
  Meh,
  Smile,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import html2canvas from 'html2canvas';
import { projectId, publicAnonKey } from '/utils/supabase/info';
import { useAuth } from '../lib/auth-context';

type FeedbackType = 'bug' | 'feature' | 'improvement' | 'praise' | 'other';
type Mood = 'frustrated' | 'confused' | 'neutral' | 'hopeful' | 'delighted' | null;

const MOODS: { value: Mood; label: string; icon: typeof Frown; emoji: string }[] = [
  { value: 'frustrated', label: 'Frustrated', icon: Frown, emoji: '😤' },
  { value: 'confused', label: 'Confused', icon: Meh, emoji: '😕' },
  { value: 'neutral', label: 'Neutral', icon: Meh, emoji: '😐' },
  { value: 'hopeful', label: 'Hopeful', icon: Smile, emoji: '🙂' },
  { value: 'delighted', label: 'Delighted', icon: Heart, emoji: '😍' },
];

function toBackendType(t: string): 'bug' | 'feature' | 'general' {
  if (t === 'bug') return 'bug';
  if (t === 'feature') return 'feature';
  return 'general';
}

function getContextualPrompt(type: FeedbackType, mood: Mood): string {
  if (type === 'bug') {
    if (mood === 'frustrated') return "We're sorry things aren't working. What happened? We'll fix it.";
    if (mood === 'confused') return "What didn't make sense? We'll clarify.";
    return 'Describe what went wrong—every detail helps.';
  }
  if (type === 'feature') {
    if (mood === 'delighted') return "Love the enthusiasm! What feature would make this even better?";
    if (mood === 'hopeful') return "We're listening. Describe the feature you'd love to see.";
    return "What would make TENSION more useful for you?";
  }
  if (type === 'praise') {
    if (mood === 'delighted') return "You made our day! What do you love most?";
    return "Tell us what's working well—it helps us know what to keep.";
  }
  if (type === 'improvement') return "How could we make this better? No idea is too small.";
  return 'Share your thoughts—we read everything.';
}

function getThankYouMessage(type: FeedbackType): { title: string; sub: string } {
  switch (type) {
    case 'bug':
      return { title: "We're on it.", sub: "We'll prioritize this fix. Thanks for helping us improve." };
    case 'feature':
      return { title: "Love this idea.", sub: "Adding it to our roadmap. We'll keep you posted." };
    case 'praise':
      return { title: "You made our day!", sub: "Your feedback fuels our work. Thank you." };
    case 'improvement':
      return { title: "Noted.", sub: "We'll look at this. Every suggestion counts." };
    default:
      return { title: "Got it.", sub: "Thanks for taking the time. We appreciate you." };
  }
}

const STEPS = [
  { id: 'welcome', label: 'Start' },
  { id: 'type', label: 'Type' },
  { id: 'details', label: 'Details' },
  { id: 'done', label: 'Done' },
];

export function BetaFeedbackWidget() {
  const { user, accessToken } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('other');
  const [mood, setMood] = useState<Mood>(null);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [rating, setRating] = useState(0);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showExitPrompt, setShowExitPrompt] = useState(false);

  const canProceed = step === 0 || (step === 1 && feedbackType) || (step === 2 && message.trim());
  const isLastStep = step === 2;

  const resetAndClose = useCallback(() => {
    setStep(0);
    setFeedbackType('other');
    setMood(null);
    setSubject('');
    setMessage('');
    setRating(0);
    setScreenshot(null);
    setSubmitted(false);
    setShowExitPrompt(false);
    setIsOpen(false);
  }, []);

  const handleClose = useCallback(() => {
    const hasContent = message.trim() || subject.trim() || screenshot;
    if (hasContent && !submitted) {
      setShowExitPrompt(true);
    } else {
      resetAndClose();
    }
  }, [message, subject, screenshot, submitted, resetAndClose]);

  const handleCaptureScreenshot = useCallback(async () => {
    try {
      toast.info('Capturing screenshot...');
      const canvas = await html2canvas(document.body, {
        allowTaint: true,
        useCORS: true,
        logging: false,
        scale: 0.75,
      });
      const dataUrl = canvas.toDataURL('image/png', 0.8);
      setScreenshot(dataUrl);
      toast.success('Screenshot captured!');
    } catch (e) {
      console.error('Screenshot error:', e);
      toast.error('Could not capture screenshot');
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!message.trim()) {
      toast.error('Please share your feedback');
      return;
    }

    setSubmitting(true);
    try {
      let fullMessage = subject.trim() ? `Subject: ${subject.trim()}\n\n${message.trim()}` : message.trim();
      if (mood) fullMessage += `\n\n[Mood: ${mood}]`;
      if (rating > 0) fullMessage += `\n[Rating: ${rating}/5]`;

      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/feedback`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${publicAnonKey}`,
            ...(accessToken ? { 'X-User-Token': accessToken } : {}),
          },
          body: JSON.stringify({
            type: toBackendType(feedbackType),
            message: (fullMessage + (screenshot ? '\n\n[Screenshot captured with this feedback]' : '')).slice(0, 5000).trim(),
            userEmail: user?.email ?? '',
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            url: window.location.href,
          }),
        }
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error ?? `Request failed: ${res.status}`);
      }

      setSubmitted(true);
      setStep(3);

      if (feedbackType === 'praise' || feedbackType === 'feature') {
        confetti({
          particleCount: 80,
          spread: 60,
          origin: { y: 0.6 },
          colors: ['#8b5cf6', '#a78bfa', '#c4b5fd'],
        });
      }
      toast.success('Thank you!');
    } catch (error) {
      console.error('Feedback submission error:', error);
      toast.error('Failed to submit. Please try again or use Support.');
    } finally {
      setSubmitting(false);
    }
  }, [message, subject, mood, rating, screenshot, feedbackType, user?.email, accessToken]);

  const goNext = useCallback(() => {
    if (step === 2) handleSubmit();
    else if (step < 2) setStep((s) => s + 1);
  }, [step, handleSubmit]);

  const goBack = useCallback(() => {
    if (step > 0) setStep((s) => s - 1);
  }, [step]);

  const encouragement = message.length >= 50
    ? 'Every detail helps—thanks!'
    : message.length >= 20
    ? 'Almost there...'
    : '';

  // This widget is for signed-in product feedback. Keep it off public marketing
  // pages so it never competes with the landing page hero/CTA or footer.
  if (!user) {
    return null;
  }

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed right-[calc(1rem+env(safe-area-inset-right))] bottom-[calc(5.75rem+env(safe-area-inset-bottom))] z-50 rounded-full bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-600 p-4 text-white shadow-xl shadow-violet-500/25 transition-all duration-300 hover:shadow-2xl hover:shadow-violet-500/30 active:scale-95 group md:right-[calc(1.5rem+env(safe-area-inset-right))] md:bottom-[calc(1.5rem+env(safe-area-inset-bottom))]"
          aria-label="Beta Feedback"
        >
          <MessageSquare className="w-6 h-6" />
          <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-amber-400 rounded-full animate-pulse ring-2 ring-white dark:ring-neutral-900" />
          <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-neutral-900 text-white text-sm rounded-xl whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg">
            Share feedback
            <div className="absolute bottom-0 right-4 transform translate-y-full border-4 border-transparent border-t-neutral-900" />
          </div>
        </button>
      )}

      {/* Feedback Panel */}
      {isOpen && (
        <div
          className="fixed right-[calc(1rem+env(safe-area-inset-right))] bottom-[calc(5.75rem+env(safe-area-inset-bottom))] z-50 w-[400px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-neutral-200/80 bg-white shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-300 dark:border-neutral-700/80 dark:bg-neutral-900 md:right-[calc(1.5rem+env(safe-area-inset-right))] md:bottom-[calc(1.5rem+env(safe-area-inset-bottom))]"
          role="dialog"
          aria-labelledby="feedback-title"
        >
          {/* Progress bar */}
          <div className="h-1 bg-neutral-100 dark:bg-neutral-800">
            <div
              className="h-full bg-gradient-to-r from-violet-500 to-purple-500 transition-all duration-500 ease-out"
              style={{ width: `${((step + 1) / 4) * 100}%` }}
            />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200/80 dark:border-neutral-700/80">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div>
                <h2 id="feedback-title" className="font-semibold text-neutral-900 dark:text-white">
                  {step === 0 ? "We're listening" : step === 3 ? "Thanks!" : 'Beta Feedback'}
                </h2>
                <p className="text-xs text-neutral-500">
                  Step {Math.min(step + 1, 3)} of 3
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 rounded-lg text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 dark:hover:text-neutral-400 dark:hover:bg-neutral-800 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 max-h-[420px] overflow-y-auto">
            {/* Step 0: Welcome */}
            {step === 0 && (
              <div className="space-y-6 animate-in fade-in duration-300">
                <p className="text-neutral-600 dark:text-neutral-400 text-sm">
                  Your feedback shapes TENSION. Report a bug, suggest a feature, or tell us what you love.
                </p>
                <div className="flex gap-2">
                  <Button onClick={() => { setFeedbackType('bug'); setStep(1); }} variant="outline" className="flex-1 gap-2">
                    <Bug className="w-4 h-4 text-red-500" />
                    Bug
                  </Button>
                  <Button onClick={() => { setFeedbackType('feature'); setStep(1); }} variant="outline" className="flex-1 gap-2">
                    <Lightbulb className="w-4 h-4 text-amber-500" />
                    Feature
                  </Button>
                  <Button onClick={() => { setFeedbackType('praise'); setStep(1); }} variant="outline" className="flex-1 gap-2">
                    <Heart className="w-4 h-4 text-pink-500" />
                    Praise
                  </Button>
                </div>
                <Button
                  onClick={() => setStep(1)}
                  variant="ghost"
                  className="w-full text-neutral-500"
                >
                  Something else
                </Button>
              </div>
            )}

            {/* Step 1: Type + Mood */}
            {step === 1 && (
              <div className="space-y-5 animate-in fade-in duration-300">
                <div>
                  <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">What kind of feedback?</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { value: 'bug' as const, label: 'Bug', icon: Bug, color: 'red' },
                      { value: 'feature' as const, label: 'Feature', icon: Lightbulb, color: 'amber' },
                      { value: 'improvement' as const, label: 'Improvement', icon: Zap, color: 'violet' },
                      { value: 'praise' as const, label: 'Praise', icon: ThumbsUp, color: 'green' },
                      { value: 'other' as const, label: 'Other', icon: MessageSquare, color: 'neutral' },
                    ].map((t) => {
                      const Icon = t.icon;
                      const active = feedbackType === t.value;
                      return (
                        <button
                          key={t.value}
                          onClick={() => setFeedbackType(t.value)}
                          className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                            active
                              ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300'
                              : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300'
                          }`}
                        >
                          <Icon className={`w-4 h-4 ${active ? 'text-violet-600' : 'text-neutral-500'}`} />
                          <span className="text-sm font-medium">{t.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">How are you feeling?</p>
                  <div className="flex flex-wrap gap-2">
                    {MOODS.map((m) => (
                      <button
                        key={m.value}
                        onClick={() => setMood(m.value)}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm transition-all ${
                          mood === m.value
                            ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 ring-2 ring-violet-500/50'
                            : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-700'
                        }`}
                      >
                        <span>{m.emoji}</span>
                        <span>{m.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Details */}
            {step === 2 && (
              <div className="space-y-4 animate-in fade-in duration-300">
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  {getContextualPrompt(feedbackType, mood)}
                </p>
                <div>
                  <label className="text-xs font-medium text-neutral-500 mb-1 block">Subject (optional)</label>
                  <Input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Brief summary..."
                    className="bg-neutral-50 dark:bg-neutral-800"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-500 mb-1 block">Details *</label>
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={getContextualPrompt(feedbackType, mood)}
                    rows={4}
                    className="bg-neutral-50 dark:bg-neutral-800 resize-none"
                  />
                  {encouragement && (
                    <p className="text-xs text-violet-600 dark:text-violet-400 mt-1">{encouragement}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-500 mb-1 block">Add a screenshot?</label>
                  {screenshot ? (
                    <div className="relative rounded-lg overflow-hidden border border-neutral-200 dark:border-neutral-700">
                      <img src={screenshot} alt="Screenshot" className="w-full h-24 object-cover" />
                      <Button
                        variant="secondary"
                        size="sm"
                        className="absolute top-1 right-1"
                        onClick={() => setScreenshot(null)}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <Button variant="outline" size="sm" onClick={handleCaptureScreenshot} className="gap-2">
                      <Camera className="w-4 h-4" />
                      Capture screenshot
                    </Button>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-500 mb-1 block">Rating (optional)</label>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <button
                        key={s}
                        onClick={() => setRating(s)}
                        className="p-1 transition-transform"
                      >
                        <Star
                          className={`w-6 h-6 ${
                            s <= rating ? 'text-amber-400 fill-amber-400' : 'text-neutral-300 dark:text-neutral-600'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Thank you */}
            {step === 3 && submitted && (
              <div className="py-6 text-center space-y-4 animate-in zoom-in-95 fade-in duration-500">
                <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
                  <ThumbsUp className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                    {getThankYouMessage(feedbackType).title}
                  </h3>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                    {getThankYouMessage(feedbackType).sub}
                  </p>
                </div>
                <Button onClick={resetAndClose} variant="outline" className="w-full">
                  Close
                </Button>
              </div>
            )}
          </div>

          {/* Footer - navigation */}
          {step < 3 && (
            <div className="flex gap-2 p-4 border-t border-neutral-200/80 dark:border-neutral-700/80">
              {step > 0 && (
                <Button variant="ghost" onClick={goBack} disabled={submitting}>
                  Back
                </Button>
              )}
              <Button
                onClick={goNext}
                disabled={!canProceed || submitting}
                className="flex-1 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 gap-2"
              >
                {submitting ? (
                  'Sending...'
                ) : isLastStep ? (
                  <>
                    <Send className="w-4 h-4" />
                    Send feedback
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Exit prompt overlay */}
          {showExitPrompt && (
            <div className="absolute inset-0 bg-white/95 dark:bg-neutral-900/95 backdrop-blur-sm flex flex-col items-center justify-center p-6 rounded-2xl animate-in fade-in duration-200">
              <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Before you go...
              </p>
              <p className="text-xs text-neutral-500 text-center mb-4">
                You have unsent feedback. Discard or continue?
              </p>
              <div className="flex gap-2 w-full">
                <Button variant="outline" onClick={() => setShowExitPrompt(false)} className="flex-1">
                  Continue
                </Button>
                <Button variant="destructive" onClick={resetAndClose} className="flex-1">
                  Discard
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
